// frontend/src/utils/backendUrl.js
// Single source of truth for the backend base URL (CRA-compatible)
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../services/firebaseConfig';
import { API_BASE_SENTINEL, resolveBackendConfig } from './backendConfig';
import { getSafeErrorCategory, getSafeFirebaseErrorCode, logAuthDiagnostic } from './authRecovery';

const BACKEND_ERROR_BANNER_ID = 'affinityai-backend-config-banner';
const BACKEND_CONFIG_LOG_ONCE_KEY = '__AFFINITYAI_BACKEND_CONFIG_LOG_ONCE__';
const env = typeof process !== 'undefined' ? process.env : {};
const host = typeof window !== 'undefined' ? window.location.hostname : '';
const BACKEND_CONFIG = resolveBackendConfig({ env, hostname: host });

export function getBackendUrl() {
  return BACKEND_CONFIG.baseUrl;
}

export const API_BASE = getBackendUrl();

function getBackendMisconfigurationMessage(path = '') {
  const baseMessage =
    BACKEND_CONFIG.message ||
    `[AffinityAI] Backend URL is not configured for ${BACKEND_CONFIG.runtimeEnv} builds.`;
  if (!path) return baseMessage;
  return `${baseMessage} Refusing API request for "${path}".`;
}

function showBackendConfigBanner(message) {
  if (typeof document === 'undefined') return;
  if (document.getElementById(BACKEND_ERROR_BANNER_ID)) return;

  const render = () => {
    if (document.getElementById(BACKEND_ERROR_BANNER_ID)) return;
    const banner = document.createElement('div');
    banner.id = BACKEND_ERROR_BANNER_ID;
    banner.textContent = message;
    banner.setAttribute('role', 'alert');
    banner.style.cssText = [
      'position:fixed',
      'top:0',
      'left:0',
      'right:0',
      'z-index:2147483647',
      'background:#991b1b',
      'color:#ffffff',
      'padding:10px 14px',
      'font:600 12px/1.4 system-ui,-apple-system,Segoe UI,Roboto,sans-serif',
      'letter-spacing:0.01em',
      'box-shadow:0 2px 8px rgba(0,0,0,0.22)',
      'text-align:center',
    ].join(';');
    document.body.appendChild(banner);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', render, { once: true });
    return;
  }
  render();
}

function notifyBackendConfigIssue() {
  if (typeof window === 'undefined') return;
  if (!BACKEND_CONFIG.isSentinel) return;

  const message = getBackendMisconfigurationMessage();
  try {
    throw new Error(message);
  } catch {
    // eslint-disable-next-line no-console
    console.error('backend_configuration_invalid', {
      errorCategory: 'backend_configuration_invalid',
    });
  }
  showBackendConfigBanner(message);
}

export function apiUrl(path) {
  if (API_BASE === API_BASE_SENTINEL) {
    throw new Error(getBackendMisconfigurationMessage(path));
  }
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${API_BASE}${p}`;
}

const AUTH_READY_TIMEOUT_MS = 1500;
let authReadyPromise;
let authReadyResolved = false;

function waitForAuthReady() {
  if (authReadyResolved) return Promise.resolve();
  if (authReadyPromise) return authReadyPromise;

  authReadyPromise = new Promise((resolve) => {
    let settled = false;
    let timeoutId;
    let unsubscribe;
    const finish = (fromTimeout) => {
      if (settled) return;
      settled = true;
      if (timeoutId) clearTimeout(timeoutId);
      if (unsubscribe) unsubscribe();
      if (fromTimeout) {
        authReadyPromise = null;
      } else {
        authReadyResolved = true;
      }
      resolve();
    };

    if (auth?.currentUser) {
      finish(false);
      return;
    }

    timeoutId = setTimeout(() => finish(true), AUTH_READY_TIMEOUT_MS);

    try {
      unsubscribe = onAuthStateChanged(auth, () => {
        finish(false);
      });
    } catch {
      finish(true);
    }
  });

  return authReadyPromise;
}

async function buildFetchOptionsWithAuth(init) {
  let options = init ? { ...init } : undefined;
  const supportsHeaders = typeof Headers !== 'undefined';
  const initHeaders = init?.headers;
  const headers = supportsHeaders
    ? new Headers(initHeaders || undefined)
    : Array.isArray(initHeaders)
      ? initHeaders.reduce((acc, [key, value]) => {
          acc[key] = value;
          return acc;
        }, {})
      : { ...(initHeaders || {}) };

  let token;
  try {
    // Attach Firebase ID token only when a user is logged in.
    await waitForAuthReady();
    token = await auth.currentUser?.getIdToken?.();
  } catch (error) {
    logAuthDiagnostic('token.failure', {
      surface: 'api_fetch',
      code: getSafeFirebaseErrorCode(error),
      category: getSafeErrorCategory(error),
    });
    token = null;
  }

  const hasAuthorization = supportsHeaders
    ? headers.has('Authorization')
    : Object.keys(headers).some((key) => key.toLowerCase() === 'authorization');
  if (token && !hasAuthorization) {
    if (supportsHeaders) headers.set('Authorization', `Bearer ${token}`);
    else headers.Authorization = `Bearer ${token}`;
  }

  if (token || initHeaders) {
    if (!options) options = {};
    options.headers = headers;
  }

  return { options, token };
}

function logAuthDebug({ path, token }) {
  const debugEnabled =
    (typeof process !== 'undefined' && (process.env.LOG_LEVEL || process.env.DEBUG)) ||
    (typeof window !== 'undefined' && (window.LOG_LEVEL || window.DEBUG));
  if (debugEnabled && typeof window !== 'undefined') {
    // eslint-disable-next-line no-console
    console.info('[apiFetch.auth]', { hasAuth: Boolean(token), path });
  }
}

// Light wrapper so callers can switch over incrementally
export async function apiFetch(path, init) {
  if (API_BASE === API_BASE_SENTINEL) {
    return Promise.reject(new Error(getBackendMisconfigurationMessage(path)));
  }

  const url = apiUrl(path);

  // eslint-disable-next-line no-console
  if (typeof window !== 'undefined') console.info('api_request_started', { route: path });

  const { options, token } = await buildFetchOptionsWithAuth(init);
  logAuthDebug({ path, token });

  // Note: leaving mode default keeps normal browser CORS behavior.
  return options ? fetch(url, options) : fetch(url);
}

export async function sameOriginApiFetch(path, init) {
  const relativePath = path?.startsWith('/') ? path : `/${path || ''}`;
  if (!relativePath.startsWith('/api/')) {
    return Promise.reject(
      new Error(`[AffinityAI] Refusing same-origin API request for "${path}".`),
    );
  }

  if (
    BACKEND_CONFIG.runtimeEnv === 'preview' ||
    BACKEND_CONFIG.runtimeEnv === 'production'
  ) {
    return Promise.reject(
      new Error('[AffinityAI] Same-origin API fallback is disabled.'),
    );
  }

  // eslint-disable-next-line no-console
  if (typeof window !== 'undefined') {
    console.info('api_request_started', { route: relativePath });
  }

  const { options, token } = await buildFetchOptionsWithAuth(init);
  logAuthDebug({ path: relativePath, token });

  return options ? fetch(relativePath, options) : fetch(relativePath);
}

// Log once on startup
if (typeof window !== 'undefined') {
  if (!window[BACKEND_CONFIG_LOG_ONCE_KEY]) {
    window[BACKEND_CONFIG_LOG_ONCE_KEY] = true;
    // eslint-disable-next-line no-console
    console.info('backend_configuration_loaded', {
      status: BACKEND_CONFIG.isSentinel ? 'invalid' : 'ready',
    });
    notifyBackendConfigIssue();
  }
}
